module.exports = angular.module('cartographer.map.attributelayer', [])

_ = require 'underscore'

module.exports.factory 'crtAttributeBucket', $DI (
  crtLog
  crtMarkerColor
  crtMarker
  crtAttributeValueFilter
) ->
  # crtAttributeLayers have a set of `buckets` that they use to categorise values for display.
  #
  # Buckets are either "value buckets" that apply to a single value,
  # or "range buckets" that apply to a range of values.
  #
  # Range buckets have an optional bounds: minValue and maxValue.
  # Setting a bound to `undefined` means the range extends to infinity.
  class crtAttributeBucket
    constructor: (@color, @label) ->
      # Do nothing

    contains: (feature) ->
      false

  class crtAttributeValueBucket extends crtAttributeBucket
    constructor: (@value, color, label) ->
      super(color, label)
      unless @color then crtMarkerColor.blank
      unless @label then @label = crtAttributeValueFilter(@value)

    # any -> boolean
    contains: (value) ->
      if value? then value == @value else !@value?

  class crtAttributeRangeBucket extends crtAttributeBucket
    constructor: (@minValue, @maxValue, @maxInclusive, color, label) ->
      super(color, label)
      unless @color then crtMarkerColor.blank
      unless @label
        if @minValue?
          if @maxValue?
            if @minValue == @maxValue
              @label = "#{crtAttributeValueFilter(@minValue)}"
            else
              @label = "#{crtAttributeValueFilter(@minValue)} to #{crtAttributeValueFilter(@maxValue)}"
          else
            @label = ">#{crtAttributeValueFilter(@minValue)}"
        else
          if @maxValue?
            @label = "<#{crtAttributeValueFilter(@maxValue)}"
          else
            @label = "All values"

    # any -> boolean
    contains: (value) ->
      if value?
        minMatches = if @minValue? then value >= @minValue else true
        maxMatches = if @maxValue? then (if @maxInclusive then value <= @maxValue else value < @maxValue) else true
        minMatches && maxMatches
      else
        (if @minValue? || @maxValue? then false else true)

  class crtAttributeBuckets
    constructor: (@id, @name, @unit, @buckets) ->
      # Do nothing

    # feature -> any
    value: (feature) =>
      feature?.attributes[@id]

    # feature -> crtAttributeBucket
    bucket: (feature) =>
      value = @value(feature)
      for bucket in @buckets when bucket.contains(value)
        return bucket
      return null

    # feature [boolean] [natural] -> string
    printedValue: (feature) ->
      if feature?
        bucket = @bucket(feature)
        if bucket instanceof crtAttributeValueBucket
          crtAttributeValueFilter(bucket.label, @unit, 2)
        else
          crtAttributeValueFilter(@value(feature), @unit, 2)
      else
        ""

    # feature [boolean] -> marker
    createPath: (feature, selected = false) =>
      bucket = @bucket(feature)
      marker = crtMarker.createPath(feature.geometry, bucket?.color, bucket?.label, selected)
      # crtLog.debug('crtAttributeLayer.createPath', { feature, selected, bucket, color: bucket?.color, marker })
      marker

  # any [string] [string] -> crtAttributeBucket
  valueBucket = (value, color, label) ->
    new crtAttributeValueBucket(value, color, label)

  # or(any undefined) or(any undefined) boolean [string] [string] -> crtAttributeBucket
  rangeBucket = ({ minValue, maxValue, maxInclusive, color, label }) ->
    # crtLog.debug('crtAttributeLayer.rangeBucket', minValue, maxValue)
    new crtAttributeRangeBucket(minValue, maxValue, maxInclusive ? (minValue == maxValue), color, label)

  # -> crtAttributeBucket
  defaultBucket = ->
    rangeBucket({
      minValue: undefined,
      maxValue: undefined,
      color: crtMarkerColor.complete,
      label: 'All surveys'
    })

  # string string or(string, null) arrayOf(crtAttributeBucket) -> crtAttributeBuckets
  fixedBuckets = (id, name, unit, buckets) ->
    new crtAttributeBuckets(id, name, unit, buckets)

  # [string] -> crtAttributeBuckets
  defaultBuckets = (id = 'default', name = 'All surveys') ->
    fixedBuckets(id, name, undefined, [ defaultBucket() ])

  # string string or(string, undefined) arrayOf(any) -> crtAttributeBuckets
  autoLookupBuckets = (id, name, unit, values) ->
    uniqueValues = _.chain(values)
      .sortBy((x) -> x)
      .uniq()
      .value()

    if uniqueValues.length == 0
      buckets = [ defaultBucket() ]
      # crtLog.debug('crtAttributeBucket.autoLookupBuckets', values, uniqueValues, buckets)
      fixedBuckets(id, name, undefined, buckets)
    else
      buckets = for value, i in uniqueValues
        valueBucket(value, crtMarkerColor.lookup(i), crtAttributeValueFilter(value))
      crtLog.debug('crtAttributeBucket.autoLookupBuckets', values, uniqueValues, buckets.map((bucket) -> bucket.color.fill))
      fixedBuckets(id, name, unit, buckets)

  # string string or(string, undefined) arrayOf(any) -> crtAttributeBuckets
  autoRangeBuckets = (id, name, unit, values) ->
    maxValue = Number.NEGATIVE_INFINITY
    minValue = Number.POSITIVE_INFINITY

    for curValue in values
      if curValue? && curValue > maxValue then maxValue = curValue
      if curValue? && curValue < minValue then minValue = curValue

    if !_.isFinite(minValue) || !_.isFinite(maxValue)
      defaultBuckets(@id, @name)
    else if minValue == maxValue
      # Generate one bucket:
      fixedBuckets(id, name, unit, [ rangeBucket(
        minValue: minValue
        maxValue: undefined
        color: crtMarkerColor.numeric(0.5)
        label: "#{crtAttributeValueFilter(minValue)} to #{crtAttributeValueFilter(maxValue)}"
      )])
    else
      # Generate ten buckets:
      numSteps = 10
      stepInc  = (maxValue - minValue) / numSteps
      buckets  = for i in [0...numSteps]
        rangeBucket({
          minValue: minValue + (i + 0) * stepInc
          maxValue: minValue + (i + 1) * stepInc
          maxInclusive: i == numSteps - 1 # last bucket is inclusive
          color: crtMarkerColor.numeric(i / numSteps)
          label: undefined # assigned automatically in crtAttributeBucket
        })

      fixedBuckets(id, name, unit, buckets)

  return {
    valueBucket
    rangeBucket
    defaultBucket
    fixedBuckets
    defaultBuckets
    autoLookupBuckets
    autoRangeBuckets
    # Exposed for tests:
    crtAttributeBucket
    crtAttributeValueBucket
    crtAttributeRangeBucket
  }

module.exports.factory 'crtAttributeLayer', $DI (
  crtLog
  crtMarkerColor
  crtAttributeBucket
  crtAttributeValueFilter
  crtSurveyStatus
) ->
  class crtAttributeLayer
    constructor: (@id, @name, @unit) ->
      # Do nothing

    # feature -> any
    value: (feature) =>
      feature?.attributes[@id]

    # feature [boolean] [natural] -> string
    printedValue: (feature) =>
      if feature? then crtAttributeValueFilter(@value(feature), @unit, 2) else ""

    # arrayOf(feature) -> crtAttributeBuckets
    buckets: (features) =>
      crtAttributeBucket.defaultBuckets(@id, @name, @unit)

  class crtDefaultAttributeLayer extends crtAttributeLayer
    constructor: (id, name, unit) ->
      super(id, name, unit)

    # arrayof(feature) -> crtAttributeBuckets
    buckets: (features) =>
      crtAttributeBucket.defaultBuckets(@id, @name)

  class crtSimpleAttributeLayer extends crtAttributeLayer
    constructor: (id, name, unit, buckets) ->
      super(id, name, unit)
      @_buckets = crtAttributeBucket.fixedBuckets(@id, @name, @unit, buckets)

    # feature -> string
    printedValue: (feature) =>
      if feature? then @_buckets.printedValue(feature, @unit, 2) else ""

    # arrayof(feature) -> crtAttributeBuckets
    buckets: (features) =>
      @_buckets

  class crtAutoAttributeLayer extends crtAttributeLayer
    constructor: (id, name, unit, @ranged = false) ->
      super(id, name, unit)

    # arrayof(feature) -> crtAttributeBuckets
    buckets: (features) =>
      if @ranged
        crtAttributeBucket.autoRangeBuckets(@id, @name, @unit, _.map(features, @value))
      else
        crtAttributeBucket.autoLookupBuckets(@id, @name, @unit, _.map(features, @value))

  # [string] -> crtAttributeLayer
  createPlaceholder = (name = "Approval status") ->
    createLookup("status", name, null, [
      { value: crtSurveyStatus.Draft    , color: crtMarkerColor.draft    , label: "Draft"    }
      { value: crtSurveyStatus.Complete , color: crtMarkerColor.complete , label: "Complete" }
      { value: crtSurveyStatus.Approved , color: crtMarkerColor.approved , label: "Approved" }
      { value: crtSurveyStatus.Review   , color: crtMarkerColor.review   , label: "Review"   }
      { value: crtSurveyStatus.Rejected , color: crtMarkerColor.rejected , label: "Rejected" }
    ])

  createLookup = (id, name, unit, lookup) ->
    new crtSimpleAttributeLayer id, name, unit, _.map lookup, (entry) ->
      crtAttributeBucket.valueBucket(entry.value, entry.color, entry.label)

  createBooleanLookup = (id, name, yesColor = "#7f0", noColor = "rgba(128, 128, 128, 0.5)") ->
    createLookup( id, name, null, [
      { value: true  , color: crtMarkerColor.create(yesColor) , label: "Yes" }
      { value: false , color: crtMarkerColor.create(noColor) , label: "No"  }
    ])

  createEnumLookup = (id, name, unit, enumeration) ->
    numValues = enumeration.values.length
    createLookup(
      id
      name
      unit
      for value, index in enumeration.values
        hue = Math.floor(360.0 * index / numValues)
        { value: value.id, color: crtMarkerColor.hue(hue), label: value.name }
    )

  createEnumPresenceLookups = (id, name, unit, enumeration) ->
    for value, index in enumeration.values
      createLookup("#{id}.#{value.id}", "#{name}: #{value.name}", unit, [
        { value: true,  color: '#0f0', label: "Present" }
        { value: false, color: '#f00', label: "Absent"  }
      ])

  # string string or(string, null) arrayOf({ value: any, color: string, label: or(string, null) }) -> crtAttributeLayer
  createThreshold = (id, name, unit, thresholds) ->
    # crtLog.debug('crtAttributeLayer.createThreshold', id, name, unit, thresholds)

    if thresholds.length == 0
      new crtSimpleAttributeLayer(id, name, unit, [
        crtAttributeBucket.rangeBucket(
          minValue: undefined
          maxValue: undefined
          color: crtMarkerColor.blank
          label: undefined # assigned automatically in crtAttributeBucket
        )
      ])
    else
      thresholds = _.sortBy thresholds, (threshold) ->
        threshold.value

      thresholdPairs = _.zip(
        thresholds
        _.tail(thresholds).concat([ null ])
      )

      buckets = _.map thresholdPairs, ([ prev, next ]) ->
        crtAttributeBucket.rangeBucket({
          minValue: prev?.value
          maxValue: next?.value
          color: prev?.color
          label: prev?.label
        })

      new crtSimpleAttributeLayer(id, name, unit, buckets)

  # string string or(string, null) arrayOf({ minValue: any, maxValue: any, color: string, label: or(string, null) }) -> crtAttributeLayer
  createRange = (id, name, unit, ranges) ->
    # crtLog.debug('crtAttributeLayer.createThreshold', id, name, unit, ranges)

    if ranges.length == 0
      new crtSimpleAttributeLayer(id, name, unit, [
        crtAttributeBucket.rangeBucket({
          minValue: undefined
          maxValue: undefined
          color: crtMarkerColor.blank
          label: undefined # assigned automatically in crtAttributeBucket
        })
      ])
    else
      ranges = _.sortBy ranges, (range) ->
        range.minValue ? Number.NEGATIVE_INFINITY

      buckets = _.map ranges, (range, index) ->
        crtAttributeBucket.rangeBucket({
          minValue: range?.minValue
          maxValue: range?.maxValue
          maxInclusive: index == ranges.length - 1 # last bucket is inclusive
          color: range?.color
          label: range?.label
        })

      new crtSimpleAttributeLayer(id, name, unit, buckets)

  createAutoLookup = (id, name, unit) ->
    new crtAutoAttributeLayer(id, name, unit, false)

  createAutoThreshold = (id, name, unit) ->
    new crtAutoAttributeLayer(id, name, unit, true)

  return {
    createPlaceholder
    createLookup
    createBooleanLookup
    createEnumLookup
    createEnumPresenceLookups
    createThreshold
    createRange
    createAutoLookup
    createAutoThreshold
  }

module.exports.filter 'crtAttributeValue', $DI (crtNumber) ->
  (value, unit = null, dp = 2) ->
    ans = switch typeof value
      when 'boolean'
        if value then 'Yes' else 'No'
      when 'string'
        if value == '' then '-' else value
      when 'number'
        if crtNumber.isInteger(value)
          value.toFixed(0)
        else if value > -1 && value < 1
          value.toPrecision(dp)
        else
          value.toFixed(dp)
      else
        if value? then "#{value}" else "-"
    if unit && value? then "#{ans} #{unit}" else "#{ans}"
